<script lang="ts" setup>
  import { cn } from "@/lib/utils"
  import {
    StepperRoot,
    useForwardPropsEmits,
    type StepperRootEmits,
    type StepperRootProps,
  } from "reka-ui"
  import { computed, type HTMLAttributes } from "vue"
  import { Badge } from "@/components/ui/badge"

  import StepperDescription from "./StepperDescription.vue"
  import StepperItem from "./StepperItem.vue"
  import StepperNote from "./StepperNote.vue"
  import StepperTitle from "./StepperTitle.vue"
  import { StepItem, StepperItemState } from "./StepperTypes"

  const props = withDefaults(
    defineProps<
      StepperRootProps & {
        class?: HTMLAttributes["class"]
        steps?: StepItem[]
      }
    >(),
    { steps: () => [] },
  )

  const emits = defineEmits<StepperRootEmits>()

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)

  // The current step is the active one if a step is marked active, otherwise
  // the first one that isn't complete. It goes by state, not isCompleted,
  // which only fills the line to the next step.
  const currentIndex = computed(() => {
    const activeIndex = props.steps.findIndex(
      ({ state }) => state === StepperItemState.ACTIVE,
    )
    if (activeIndex !== -1) return activeIndex

    return props.steps.findIndex(
      ({ state }) => state !== StepperItemState.COMPLETE,
    )
  })

  // reka counts steps from 1 and reads out "Step {current} of {total}", so
  // once every step is complete it stays on the last one.
  const currentStep = computed(() =>
    currentIndex.value === -1 ? props.steps.length : currentIndex.value + 1,
  )

  // Read out after each title. A badge already names the state.
  const stateLabel = (stepItem: StepItem, index: number) => {
    if (stepItem.badge) return stepItem.badge
    if (stepItem.state === StepperItemState.COMPLETE) return "Complete"
    if (stepItem.state === StepperItemState.ERROR) return "Action needed"
    if (stepItem.state === StepperItemState.ACTIVE) return "In progress"
    if (index === currentIndex.value) return "In progress"
    return "Not started"
  }
</script>

<template>
  <StepperRoot
    :class="
      cn(
        'flex max-w-[697px] flex-col gap-4 sm:w-full sm:flex-row sm:gap-4',
        props.class,
      )
    "
    v-bind="forwarded"
    :model-value="currentStep"
  >
    <StepperItem
      v-for="(stepItem, index) in steps"
      :key="index"
      :step="index + 1"
      :completed="stepItem.state === StepperItemState.COMPLETE"
      :step-count="steps.length"
      :state="stepItem.state"
      :is-completed="stepItem.isCompleted"
      :is-animated="stepItem.isAnimated"
      :data-testid="`stepper-item-${index + 1}`"
    >
      <StepperTitle>
        {{ stepItem.title
        }}<span class="sr-only">, {{ stateLabel(stepItem, index) }}</span>
      </StepperTitle>
      <!-- Hidden from screen readers, which hear it in the title. -->
      <Badge
        v-if="stepItem.badge"
        variant="destructive"
        class="mt-1"
        aria-hidden="true"
      >
        {{ stepItem.badge }}
      </Badge>
      <template #after>
        <!-- Indented past the indicator on mobile, where the note shares the date's line. -->
        <div
          class="-mt-1 flex items-center gap-1 pl-10 sm:mt-0 sm:max-w-[132px] sm:flex-col sm:gap-0 sm:pl-0 sm:text-center"
        >
          <StepperDescription
            v-if="stepItem.description || !stepItem.badge"
            :aria-label="stepItem.descriptionAriaLabel"
            :class="
              cn({
                'duration-500 animate-in fade-in motion-reduce:animate-none':
                  stepItem.isAnimated,
              })
            "
          >
            {{ stepItem.description }}
          </StepperDescription>
          <StepperNote v-if="stepItem.note" :note="stepItem.note" />
        </div>
      </template>
    </StepperItem>
    <slot />
  </StepperRoot>
</template>
